import React, { useEffect, useRef, useState } from 'react';

interface LazySectionProps {
  children: React.ReactNode;
  minHeight?: string;
  rootMargin?: string;
  id?: string | string[];
}

/**
 * Defers below-the-fold sections until they approach the viewport.
 * A stable placeholder keeps hash targets available and prevents the
 * remaining page from collapsing while each section is still deferred.
 */
export function LazySection({
  children,
  id,
  minHeight = "640px",
  rootMargin = "400px 0px",
}: LazySectionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const ids = Array.isArray(id) ? id : id ? [id] : [];

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setIsVisible(true);
        observer.disconnect();
      },
      { rootMargin },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin]);

  return (
    <div
      ref={containerRef}
      className="relative"
      style={{
        minHeight,
        contentVisibility: isVisible ? "visible" : "auto",
        containIntrinsicSize: `auto ${minHeight}`,
      }}
    >
      {ids.map(elemId => (
        <div key={elemId} id={elemId} className="absolute top-0 left-0" />
      ))}
      {isVisible ? children : null}
    </div>
  );
}
